import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../i18n.jsx';
import CardTilt from './CardTilt.jsx';
import Pista from './Pista.jsx';
import { cardArt } from '../cardArt.js';
import { api } from '../api.js';
import { useAvisos } from '../avisos.jsx';
import { useCierreSuave, useFocoModal, useVolverCierra } from '../movimiento.js';

const money = (n, unit) =>
  n == null ? null : new Intl.NumberFormat(undefined, { style: 'currency', currency: unit }).format(n);

const clp = (n) =>
  n == null
    ? null
    : new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        maximumFractionDigits: 0,
      }).format(n);

/**
 * La tendencia del precio EN CHILE: el minimo chileno de cada dia. Solo pesos,
 * a proposito: la serie de TCGplayer en USD no dice lo que cuesta la carta
 * aqui, y con dos mercados en la misma ficha la linea se leia como la del
 * "desde $X" de encima. El eje X va por FECHA, no por indice.
 *
 * PLANA HASTA LA VISPERA y no en diagonal de punto a punto (sep-2026): el dato
 * es "el minimo de ese dia", y ese precio vale hasta que el vigia ve otro. Una
 * diagonal entre el 3-ago y el 25-sep dibujaba una bajada lenta de siete
 * semanas que nunca existio: el precio estuvo quieto y cayo en un dia.
 *
 * INTERACTIVA: posar el raton (o arrastrar el dedo) marca el dia mas cercano
 * con su fecha y su precio; con el foco, las flechas recorren los puntos. Sin
 * eso, una linea sin ejes solo decia "subio o bajo" y no CUANDO ni A CUANTO.
 */
function Tendencia({ historia, t, lang }) {
  const [activo, setActivo] = useState(null);
  const puntos = historia.filter((p) => p.clp != null);
  if (puntos.length < 2) return null;

  const fmt = (v) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v);
  const vals = puntos.map((p) => Number(p.clp));
  const dias = puntos.map((p) => new Date(`${p.date}T12:00:00`).getTime());
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const W = 300;
  const H = 72;
  const PAD = 6;
  const x = (i) => PAD + ((dias[i] - dias[0]) * (W - 2 * PAD)) / (dias[dias.length - 1] - dias[0]);
  /*
   * ESCALA COMUN A TODAS LAS CARTAS (sep-2026). El eje Y iba de min a max de
   * cada carta, asi que cualquier cambio llenaba el alto entero: una bajada de
   * $5.000 sobre $75.000 (−7 %) se veia como un desplome, igual que una de
   * $20.000, y no habia dos graficos comparables (queja del dueño).
   * Una escala fija EN PESOS no sirve: una carta de $800 y una de $70.000 no
   * caben en el mismo eje sin que la barata salga plana siempre. Lo comparable
   * entre cartas es el cambio PROPORCIONAL, asi que el eje es logaritmico y el
   * alto representa SIEMPRE la misma ventana (×VENTANA, ≈ ±25 %) centrada en
   * la serie: un −7 % ocupa lo mismo en todas las cartas. Solo una carta que
   * se mueve mas que la ventana la ensancha — y entonces el grafico lleno
   * sigue diciendo la verdad, porque para llenarlo hay que moverse mucho.
   */
  const VENTANA = 1.6;
  const lnMin = Math.log(min);
  const lnMax = Math.log(max);
  const medio = (lnMin + lnMax) / 2;
  const semi = Math.max(Math.log(VENTANA) / 2, ((lnMax - lnMin) / 2) * 1.08);
  const y = (v) => H - PAD - ((Math.log(v) - (medio - semi)) * (H - 2 * PAD)) / (2 * semi);
  /* Pauta cada 10 % desde el primer precio: la regla visible de esa escala,
     la misma en todas las fichas. */
  const pauta = [];
  for (let k = -12; k <= 12; k++) {
    if (k === 0) continue;
    const yy = y(vals[0] * 1.1 ** k);
    if (yy > 1 && yy < H - 1) pauta.push(yy);
  }
  /*
   * Plano mientras el precio no cambia y la PENDIENTE en el ultimo dia: del
   * dia anterior al punto nuevo. Un escalon vertical clavado en el dia del
   * punto decia que el cambio ocurrio en ese instante, y el dato solo sabe que
   * ocurrio entre la lectura de ayer y la de hoy (queja del dueño con el
   * grafico delante). Si hay hueco de varios dias sin lectura, el precio
   * conocido se sostiene hasta la vispera y solo el ultimo dia se inclina.
   */
  const DIA = 86400000;
  const xt = (ms) => PAD + ((ms - dias[0]) * (W - 2 * PAD)) / (dias[dias.length - 1] - dias[0]);
  const tramos = vals.map((v, i) => {
    if (i === 0) return `M${x(0).toFixed(1)},${y(v).toFixed(1)}`;
    const vispera = dias[i] - dias[i - 1] > DIA ? `H${xt(dias[i] - DIA).toFixed(1)} ` : '';
    return `${vispera}L${x(i).toFixed(1)},${y(v).toFixed(1)}`;
  });
  const linea = tramos.join(' ');
  const area = `${linea} V${H} H${x(0).toFixed(1)} Z`;
  const fecha = (d, largo = false) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-US', {
      day: 'numeric',
      month: largo ? 'long' : 'short',
    });
  /*
   * EL VEREDICTO EN PALABRA, no solo en la pendiente: primero contra ultimo
   * punto. Por debajo del 2 % se llama "estable" -- un peso arriba o abajo en
   * una carta de $700 no es una tendencia. La palabra y la flecha dicen lo
   * mismo que el color (regla de la casa: nada de color como unico indicador).
   */
  const cambio = (vals[vals.length - 1] - vals[0]) / vals[0];
  const rumbo = Math.abs(cambio) < 0.02 ? 'plano' : cambio > 0 ? 'sube' : 'baja';
  const pct = Math.round(Math.abs(cambio) * 100);

  /* El punto mas cercano al puntero, por FECHA (la X es tiempo). */
  const masCercano = (clientX, caja) => {
    const vx = ((clientX - caja.left) / caja.width) * W;
    let mejor = 0;
    for (let i = 1; i < vals.length; i++) {
      if (Math.abs(x(i) - vx) < Math.abs(x(mejor) - vx)) mejor = i;
    }
    return mejor;
  };
  const mover = (e) => setActivo(masCercano(e.clientX, e.currentTarget.getBoundingClientRect()));
  const tecla = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    e.stopPropagation(); // las flechas de la ficha cambian de carta; aqui no
    const ult = vals.length - 1;
    setActivo((a) => {
      const i = a ?? ult;
      if (e.key === 'Home') return 0;
      if (e.key === 'End') return ult;
      return Math.max(0, Math.min(ult, i + (e.key === 'ArrowLeft' ? -1 : 1)));
    });
  };

  const px = activo != null ? (x(activo) / W) * 100 : 0;
  const py = activo != null ? (y(vals[activo]) / H) * 100 : 0;
  /* Variacion del punto marcado contra el anterior: dice si ESE dia fue un
     salto o solo la continuacion. */
  const delta = activo > 0 ? vals[activo] - vals[activo - 1] : 0;

  return (
    <section className="tendencia">
      <div className="tendencia__cab">
        <h3>
          {t('trendTitle')} <span className="tendencia__sub">· {t('trendClp')}</span>
        </h3>
        <span className={`tendencia__rumbo is-${rumbo}`}>
          <span aria-hidden="true">{rumbo === 'sube' ? '▲' : rumbo === 'baja' ? '▼' : '—'}</span>{' '}
          {rumbo === 'plano' ? t('trendFlat') : t(rumbo === 'sube' ? 'trendUp' : 'trendDown', { p: pct })}
        </span>
      </div>
      <div className="tendencia__graf">
        <div
          className={`tendencia__lienzo${activo != null ? ' is-activo' : ''}`}
          tabIndex={0}
          role="group"
          aria-label={`${t('trendTitle')}: ${fmt(vals[0])} → ${fmt(vals[vals.length - 1])}. ${t('trendKeys')}`}
          onPointerMove={mover}
          onPointerDown={mover}
          onPointerLeave={(e) => e.pointerType === 'mouse' && setActivo(null)}
          onKeyDown={tecla}
          onBlur={() => setActivo(null)}
        >
          <svg viewBox={`0 0 ${W} ${H}`} className="tendencia__svg" aria-hidden="true" preserveAspectRatio="none">
            <defs>
              <linearGradient id="tendenciaArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" style={{ stopColor: 'var(--accent)', stopOpacity: 0.26 }} />
                <stop offset="1" style={{ stopColor: 'var(--accent)', stopOpacity: 0 }} />
              </linearGradient>
            </defs>
            {pauta.map((yy) => (
              <line
                key={yy}
                x1="0"
                x2={W}
                y1={yy.toFixed(1)}
                y2={yy.toFixed(1)}
                stroke="var(--line)"
                strokeWidth="1"
                strokeDasharray="2 4"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <path d={area} fill="url(#tendenciaArea)" />
            <path
              d={linea}
              fill="none"
              stroke="var(--accent)"
              strokeWidth="1.8"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {/* Guia, punto y globo en HTML y no en el SVG: con
              `preserveAspectRatio="none"` un circulo saldria ovalado. */}
          {activo != null && (
            <>
              <span className="tendencia__guia" style={{ left: `${px}%` }} aria-hidden="true" />
              <span className="tendencia__punto" style={{ left: `${px}%`, top: `${py}%` }} aria-hidden="true" />
              <span
                className={`tendencia__globo${px < 22 ? ' is-izq' : px > 78 ? ' is-der' : ''}`}
                style={{ left: `${px}%` }}
                aria-live="polite"
              >
                <span className="tendencia__globoFecha">{fecha(puntos[activo].date, true)}</span>
                <strong>{fmt(vals[activo])}</strong>
                {delta !== 0 && (
                  <span className={`tendencia__globoDelta ${delta < 0 ? 'is-baja' : 'is-sube'}`}>
                    {delta < 0 ? '▼' : '▲'} {fmt(Math.abs(delta))}
                  </span>
                )}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="tendencia__pie muted">
        <span>{fecha(puntos[0].date)}</span>
        <span>
          {fmt(min)} – {fmt(max)}
        </span>
        <span>{fecha(puntos[puntos.length - 1].date)}</span>
      </div>
    </section>
  );
}

export default function CardDetail({ cardId, onClose, onPrev, onNext }) {
  const { lang, t } = useLang();
  const { avisar } = useAvisos();
  const [card, setCard] = useState(null);

  /*
   * AÑADIR AL MAZO desde la ficha: descubrir una tech navegando y tener que
   * volver a buscarla en el constructor era el rodeo mas tonto del flujo.
   *
   * El destino se decide por evento: si el constructor esta montado y en
   * edicion, la toma el (con su tope por nombre y su pulso) y hace
   * preventDefault; si nadie la reclama, va al BORRADOR de localStorage -- el
   * mismo que el constructor abre como "Sin guardar" -- y el aviso dice a
   * donde fue, porque un "añadida" sin destino es un misterio.
   */
  const agregarAlMazo = () => {
    if (!card) return;
    const evento = new CustomEvent('ptcg:agregar-al-mazo', {
      detail: { card },
      cancelable: true,
    });
    if (!window.dispatchEvent(evento)) return;   // la tomo el constructor
    try {
      const KEY = 'ptcg-tracker:deck';
      const bruto = JSON.parse(localStorage.getItem(KEY) ?? '[]');
      const lista = Array.isArray(bruto) ? bruto : [];
      const fila = lista.find((d) => d.cardId === card.id);
      const esBasica = card.keys?.category === 'Energy' && card.keys?.energyType === 'Normal';
      if (fila && fila.count >= 4 && !esBasica) {
        avisar('warn', t('addLimit', { card: card.name }));
        return;
      }
      if (fila) fila.count += 1;
      else lista.push({ cardId: card.id, count: 1 });
      localStorage.setItem(KEY, JSON.stringify(lista));
      avisar('ok', t('addedToDraft', { n: lista.reduce((s, d) => s + d.count, 0) }));
    } catch {
      avisar('error', t('addFailed'));
    }
  };
  const [pricing, setPricing] = useState({ state: 'idle', data: null });
  // Se deja terminar la animacion de salida antes de desmontar. Antes se
  // esperaba con un setTimeout de 180 ms copiado a mano del CSS; ahora el hook
  // escucha el final real de la animacion, que es lo mismo pero sin dos
  // numeros que mantener sincronizados.
  const { ref: refModal, cerrando, pedirCierre: requestClose } = useCierreSuave(onClose);
  useFocoModal(refModal);
  useVolverCierra(true, requestClose);
  // Carta ampliada a todo el modal.
  const [ampliada, setAmpliada] = useState(false);
  // El zoom es su propia capa: "atras" lo deshace primero, igual que Escape.
  /*
   * EL ZOOM NO APILA ENTRADA DE HISTORIAL, y antes sí.
   *
   * Parecía razonable -- "atrás" deshace el zoom antes que la ficha -- pero
   * salía caro. Con dos entradas para un solo modal, cerrarlas a la vez lanza
   * dos `history.back()` en el mismo tick; como `back()` es asíncrono, ambos
   * leen el historial antes de que ninguno se aplique y saltan de más. Medido:
   * la pila caía de 3 a 0 y se llevaba por delante la entrada del MAZO
   * ABIERTO, así que salías del zoom y aparecías en "Mis mazos" con el
   * análisis perdido. Y el salto sobrante remontaba la ficha, que repetía su
   * animación de entrada: el pestañeo del modal entero al volver del zoom.
   *
   * Una capa, una entrada. El zoom es un modo de ver la MISMA carta, no un
   * sitio nuevo al que se ha navegado.
   *
   * LO QUE SE PIERDE, y conviene saberlo: en el teléfono, el gesto de "atrás"
   * estando ampliada ahora cierra la ficha entera en vez de solo el zoom. Se
   * sigue saliendo del zoom con Escape, con el aspa y pinchando fuera, que son
   * los tres caminos de siempre y ahora deshacen un paso cada uno.
   */

  /*
   * Version elegida desde el carrusel de otras impresiones.
   *
   * Se guarda APARTE de `cardId` porque esa prop es del padre (la rejilla): al
   * pinchar una version cambia lo que se muestra sin tocar la posicion en la
   * rejilla, y las flechas de anterior/siguiente siguen refiriendose a la carta
   * original. Cambiar de carta en la rejilla resetea la version.
   */
  const [versionId, setVersionId] = useState(null);
  useEffect(() => setVersionId(null), [cardId]);
  const idActual = versionId ?? cardId;

  useEffect(() => {
    let alive = true;
    setCard(null);
    api(`/api/cards/${encodeURIComponent(idActual)}?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setCard(d));
    return () => {
      alive = false;
    };
  }, [idActual, lang]);

  // Los precios se piden aparte: vienen en vivo de TCGdex y no deben
  // retrasar el renderizado de la carta si el proveedor esta lento.
  // Por `idActual`, no por `cardId`: cada impresion tiene sus precios, y al
  // elegir otra version desde la tira (o desde la linea evolutiva) la ficha
  // entera es la de esa impresion. Con `cardId` se quedaban los de la primera.
  useEffect(() => {
    let alive = true;
    setPricing({ state: 'loading', data: null });
    api(`/api/cards/${encodeURIComponent(idActual)}/pricing`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('upstream'))))
      .then((d) => alive && setPricing({ state: 'ready', data: d }))
      .catch(() => alive && setPricing({ state: 'error', data: null }));
    return () => {
      alive = false;
    };
  }, [idActual]);

  /*
   * La TENDENCIA: el minimo chileno de cada dia, del historial del vigia (que
   * barre todas las cartas 4 veces al dia, sin esperar a que alguien las
   * mire). Mientras no haya dos puntos no se pinta nada -- una "tendencia" de
   * un punto es un adorno.
   */
  const [historia, setHistoria] = useState([]);
  useEffect(() => {
    let alive = true;
    setHistoria([]);
    // Por `idActual`, como los precios: la serie es de la impresion que se ve.
    api(`/api/cards/${encodeURIComponent(idActual)}/pricing/history`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setHistoria(d?.history ?? []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [idActual]);

  /*
   * Escape cierra PRIMERO el zoom y solo despues el modal.
   *
   * Al reves seria un fallo con consecuencias: sales de la carta ampliada y de
   * la ficha de golpe, y vuelves a la rejilla habiendo perdido lo que estabas
   * mirando. Escape deshace un paso, no dos.
   */
  useEffect(() => {
    const onKey = (e) => {
      /*
       * Flechas: carta anterior / siguiente SIN cerrar la ficha, para poder
       * "vitrinear" la rejilla desde el detalle. Solo cuando el padre puede
       * navegar (hay lista alrededor) y nunca con el zoom abierto: ahi las
       * flechas del teclado se confundirian con querer mover la carta ampliada.
       */
      if (!ampliada) {
        if (e.key === 'ArrowLeft' && onPrev) return onPrev();
        if (e.key === 'ArrowRight' && onNext) return onNext();
      }
      if (e.key !== 'Escape') return;
      if (ampliada) setAmpliada(false);
      else requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  /**
   * Bloquea el desplazamiento del fondo mientras el modal esta abierto, sin que
   * la pagina se mueva.
   *
   * `overflow: hidden` quita la barra, y en un navegador que no reserve su hueco
   * el area util se ensancha: el contenido salta unos pixeles y vuelve al cerrar.
   * La correccion es medir cuanto se ensancho de verdad y devolverlo como relleno.
   *
   * SE MIDE `body.clientWidth`, NO `html.clientWidth`. Aqui estaba el fallo: con
   * `scrollbar-gutter: stable` el hueco esta reservado siempre y la maquetacion NO
   * cambia de ancho, pero `html.clientWidth` SI cambia -- pasa a incluir el hueco
   * cuando la barra desaparece. El codigo leia ese cambio como un salto real,
   * compensaba 15 px que nadie habia perdido, y el resultado era que todo el fondo
   * se estrechaba y se corria a la izquierda al abrir cualquier carta.
   *
   * `body.clientWidth` es el ancho de maquetacion de verdad: con hueco reservado
   * sale la misma cifra antes y despues (diferencia 0, no se toca nada), y sin el
   * -- Safari anterior a 16 -- sale la diferencia real y se compensa.
   */
  useEffect(() => {
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPadding = body.style.paddingRight;

    const antes = body.clientWidth;
    body.style.overflow = 'hidden';
    const ensancho = body.clientWidth - antes;
    if (ensancho > 0) {
      const actual = parseFloat(getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${actual + ensancho}px`;
    }

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPadding;
    };
  }, []);

  /*
   * La rueda del raton desplaza la tira de versiones en HORIZONTAL.
   *
   * La tira oculta su barra en movil (se arrastra con el dedo), pero con raton
   * girar la rueda encima no hacia nada: era una fila con mas contenido y
   * ninguna forma de llegar a el. Se traduce el giro vertical a desplazamiento
   * horizontal, que es el gesto que todo el mundo intenta primero.
   *
   * Va como ref-callback y con addEventListener propio porque React registra
   * `onWheel` como pasivo y preventDefault -- necesario para que la pagina de
   * fondo no se mueva a la vez -- no funcionaria.
   */
  const refTira = useCallback((el) => {
    if (!el) return;
    // StrictMode monta dos veces en desarrollo y el ref-callback se repite
    // sobre el MISMO nodo: sin la marca, cada rueda desplazaba el doble.
    if (el.dataset.conRueda) return;
    el.dataset.conRueda = '1';
    el.addEventListener(
      'wheel',
      (e) => {
        if (el.scrollWidth <= el.clientWidth) return; // cabe entera: rueda normal
        if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // gesto ya horizontal (trackpad)
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      },
      { passive: false }
    );
  }, []);

  const cm = pricing.data?.variants?.find((v) => v.pricing?.cardmarket)?.pricing?.cardmarket;
  const tp = pricing.data?.variants?.find((v) => v.pricing?.tcgplayer)?.pricing?.tcgplayer;
  const tpMarket = tp && Object.values(tp).find((v) => v && typeof v === 'object' && v.marketPrice);

  // Portal a <body>: un ancestro con `transform` (las vistas lo tienen por su
  // animacion de entrada) captura los `position: fixed` hijos y el modal se
  // dimensionaria respecto a la vista en vez de a la ventana.
  return createPortal(
    /*
     * EL CLIC FUERA DESHACE UN PASO, NO DOS -- igual que Escape.
     *
     * Llamaba a `requestClose()` a secas, asi que con la carta AMPLIADA cerraba
     * de golpe el zoom y la ficha. Y el problema no era solo perder el sitio:
     * el zoom y la ficha apilan una entrada de historial CADA UNO, sus dos
     * `useVolverCierra` se desmontaban a la vez y sus `history.back()` salian
     * en el mismo tick. Como `back()` es asincrono, el segundo leia un estado
     * que el primero aun no habia aplicado y tiraba de mas: medido, la pila
     * pasaba de 3 a 0 -- tres saltos cuando tocaban dos -- y se llevaba por
     * delante la entrada del MAZO ABIERTO.
     *
     * Consecuencia real: mirabas una carta desde el analisis de un mazo,
     * ampliabas, pinchabas fuera y aparecias en "Mis mazos" con el analisis
     * perdido, a repetirlo.
     *
     * Cerrando de una en una nunca hay dos back() compitiendo.
     */
    <div
      className={`overlay${cerrando ? ' is-closing' : ''}`}
      onClick={() => (ampliada ? setAmpliada(false) : requestClose())}
    >
      <div
        ref={refModal}
        className={`detail${ampliada ? ' is-zoom' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        /* El nombre de la carta como etiqueta del dialogo: "dialogo, Pikachu"
           dice a que se entro; un dialogo sin nombre obliga a explorar. */
        aria-label={card?.name ?? t('loading')}
        /* El esqueleto es decorativo (aria-hidden); esto es lo que anuncia la
           carga a un lector de pantalla. */
        aria-busy={!card}
      >
        {/*
          FONDO AMBIENTAL: el arte de la carta, muy difuminado, tiñendo la
          cabecera del modal y desvaneciendose hacia abajo. Da a cada ficha la
          atmosfera de su ilustracion sin robar contraste a los datos: vive
          detras de todo (z-index 0, el cuerpo va en 1), no captura clics y se
          apaga antes de la mitad del modal. `key` por impresion: al cambiar de
          version el tinte nuevo entra con su fundido en vez de saltar.
        */}
        {card && cardArt(card, 'high') && !ampliada && (
          <div
            key={`fondo-${card.id}`}
            className="detail__fondo"
            aria-hidden="true"
            style={{ '--arte': `url("${cardArt(card, 'high')}")` }}
          />
        )}

        <button type="button" className="detail__close" onClick={requestClose} aria-label={t('close')}>
          ×
        </button>

        {/*
          Anterior / siguiente, en los BORDES del modal como en cualquier visor
          de fotos. Solo existen si el padre sabe navegar (vista de cartas, que
          tiene la rejilla alrededor); desde el mazo o el meta la ficha se abre
          suelta y no hay "al lado" que enseñar.
        */}
        {onPrev && (
          <button type="button" className="detail__nav detail__nav--prev" onClick={onPrev} aria-label={t('prevCard')}>
            ‹
          </button>
        )}
        {onNext && (
          <button type="button" className="detail__nav detail__nav--next" onClick={onNext} aria-label={t('nextCard')}>
            ›
          </button>
        )}

        {!card ? (
          /*
           * Esqueleto con la MISMA rejilla que el contenido real: silueta de
           * carta a la izquierda y barras de texto a la derecha. Antes habia un
           * "Cargando…" suelto en un modal vacio, y al navegar con las flechas
           * toda la ficha desaparecia y reaparecia: el marco no se movia (la
           * altura es fija) pero el interior si se deformaba. Con la silueta,
           * lo unico que cambia entre cartas es el relleno de cada hueco.
           */
          <div className="detail__body detail__skel" aria-hidden="true">
            <div className="detail__imagewrap">
              <div className="skel skel--carta" />
              {/*
                La pista de inclinar es TEXTO FIJO, asi que en el esqueleto va
                tal cual: asi su hueco mide exactamente lo que medira. Era la
                pieza que faltaba y por la que toda la columna se recolocaba al
                llegar la carta (va centrada en vertical: cualquier diferencia
                de alto mueve tambien lo de arriba).
              */}
              <p className="detail__tilthint muted">{t('zoomHintIn')}</p>
            </div>
            <div className="detail__info">
              <div className="skel skel--titulo" />
              <div className="skel skel--sub" />
              <div className="detail__skelchips">
                <span className="skel skel--chip" />
                <span className="skel skel--chip" />
                <span className="skel skel--chip" />
                <span className="skel skel--chip" />
              </div>
              <div className="skel skel--rotulo" />
              <div className="skel skel--linea" style={{ width: '46%' }} />
              <div className="skel skel--linea" style={{ height: 64 }} />
              <div className="skel skel--linea" />
              <div className="skel skel--linea" style={{ width: '86%' }} />
            </div>
            <div className="detail__rel">
              {/*
                La tira de "otras versiones" reusa su maqueta real (.versiones):
                mismos margenes, mismo titulo, mismas miniaturas. Las medidas
                copiadas a mano se desviaban y las miniaturas saltaban.
              */}
              <div className="versiones">
                <p className="versiones__titulo">
                  <span className="skel skel--minititulo" />
                </p>
                <ul className="versiones__tira">
                  {[0, 1, 2, 3].map((i) => (
                    <li key={i}>
                      <span className="skel skel--mini" />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="detail__body detail__cambio" key={card.id}>
            <div className="detail__imagewrap">
              <CardTilt
                src={cardArt(card, 'high')}
                preSrc={cardArt(card)}
                alt={card.name}
                /* Solo `holo`, NO `reverse`: casi toda carta comun tiene una
                   variante reverse-holo, pero la imagen que se muestra aqui es
                   la impresion normal. Incluir `reverse` ponia brillo de rara a
                   los comunes, que es justo lo que uno mira en esta pantalla.

                   Excepcion: energias cuyo arte viene del CDN de Limitless.
                   Ese arte es el RENDER DEL JUEGO, que pinta las energias
                   basicas con su patron foil -- la imagen ya se ve
                   holografica y sin el brillo parecia una foto apagada de una
                   carta brillante. El dato de variantes dice "normal" porque
                   describe la impresion fisica, no el render que se muestra. */
                holo={
                  !!card.variants?.holo ||
                  (card.keys?.category === 'Energy' &&
                    !card.image &&
                    String(card.imageFallback ?? '').includes('limitlesstcg'))
                }
                onActivate={() => setAmpliada((v) => !v)}
                etiquetaBoton={ampliada ? t('zoomOut') : t('zoomIn')}
                /* Sin arte, el hueco brillante parecia una carga que nunca
                   termina. Con texto queda claro que es la ilustracion la que
                   falta, no la aplicacion la que fallo. */
                sinArte={t('noArtCard')}
              />
              <p className="detail__tilthint muted">
                {ampliada ? t('zoomHintOut') : t('zoomHintIn')}
              </p>

            </div>

            <div className="detail__info">
              <header>
                <h2>{card.name}</h2>
                <p className="muted">
                  {card.setName} · {card.localId} · {card.labels.rarity}
                </p>
                {card.alternate && card.alternate.name !== card.name && (
                  <p className="detail__alt">
                    {lang === 'es' ? t('alternateName') : t('alternateNameEs')}:{' '}
                    <strong>{card.alternate.name}</strong>
                  </p>
                )}
                {card.isFallback && <p className="badge badge--warn">{t('fallbackNotice')}</p>}
              </header>

              {/*
                LA IDENTIDAD EN UNA SOLA FILA (sep-2026). Antes eran chips de
                PS, tipo, fase, retirada y marca, una linea de legalidad, otra
                de presencia en el meta, y debajo ataques, habilidades y
                debilidad: todo eso esta IMPRESO en la carta de al lado, que
                ademas se amplia con un clic. Repetirlo en texto empujaba el
                precio -- lo unico de la ficha que la carta no puede decir -- a
                media pantalla de scroll. Queda lo que sirve para decidir: de
                que es, en que formato vale y la marca de reglamento, que es
                lo que decide la rotacion.
              */}
              <div className="chips">
                {(card.labels.types || []).map((label) => (
                  <span className="chip" key={label}>{label}</span>
                ))}
                {card.labels.stage && <span className="chip">{card.labels.stage}</span>}
                {card.regulationMark && (
                  <span className="chip" title={t('regulationMark')}>
                    {t('filterMark')} {card.regulationMark}
                  </span>
                )}
                {card.legal.standard || card.legal.expanded ? (
                  [card.legal.standard && t('standard'), card.legal.expanded && t('expanded')]
                    .filter(Boolean)
                    .map((f) => (
                      <span className="chip chip--legal" key={f}>
                        <span aria-hidden="true">✓</span> {f}
                      </span>
                    ))
                ) : (
                  <span className="chip chip--fuera">{t('notLegal')}</span>
                )}
              </div>

              <button type="button" className="btn btn--ghost detail__addDeck" onClick={agregarAlMazo}>
                + {t('addToDeck')}
              </button>

              {/*
                EL PRECIO ES LA SECCION PRINCIPAL y va primero lo de AQUI: el
                minimo en Chile, su tendencia y las tiendas. La referencia de
                Cardmarket/TCGplayer va despues y en pequeño -- es otro mercado
                y otra moneda --, y si no hay ni eso ni tiendas se dice una sola
                vez, en vez de un "no disponible" encima de un precio que si hay.
              */}
              <section className="precio">
                {pricing.state === 'loading' && (
                  /* CALCA el caso típico —rótulo, «Desde $X», dos tiendas y la
                     nota— y no dos barras sueltas: el bloque real mide ~130 px
                     y el esqueleto viejo 80, así que la tendencia de debajo
                     bajaba de golpe al llegar el precio (~1,3 s en frío). */
                  <div className="precio__skel" aria-hidden="true">
                    <div className="skel skel--rotulo" style={{ margin: '0 0 10px', width: 64 }} />
                    <div className="skel skel--linea" style={{ width: '40%', height: 26, marginTop: 0 }} />
                    <div className="skel skel--linea" style={{ width: '100%', height: 34, marginTop: 10 }} />
                    <div className="skel skel--linea" style={{ width: '100%', height: 34, marginTop: 4 }} />
                    <div className="skel skel--linea" style={{ width: '58%', height: 10, marginTop: 10 }} />
                  </div>
                )}
                {(pricing.state === 'error' ||
                  (pricing.state === 'ready' && !cm && !tpMarket && !pricing.data?.chile)) && (
                  <p className="muted">{t('pricesUnavailable')}</p>
                )}
              {pricing.data?.chile && (
                <>
                  <h3>{t('inChile')}</h3>
                  {pricing.data.chile.cheapestClp != null && (
                    <p className="shop__from">
                      <span className="shop__desde">{t('from')}</span>{' '}
                      <strong>{clp(pricing.data.chile.cheapestClp)}</strong>
                      {/*
                        La tienda la ve todo el mundo (ago-2026). El
                        condicional se queda porque la fuente de RESPALDO
                        (chile.js, catalogo en vivo de Collector) puede no
                        traerla; sin tienda se dice "referencia del mercado
                        local" antes que enlazar a ciegas.
                      */}
                      {pricing.data.chile.shop ? (
                        <a
                          className="shop__source"
                          href={pricing.data.chile.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {pricing.data.chile.shop}
                        </a>
                      ) : (
                        <span className="muted shop__source">{t('localMarketRef')}</span>
                      )}
                      {/*
                        La cabecera prefiere una oferta de TIENDA aunque un
                        agregado este mas barato (ver ofertasDeCarta). Si sale
                        de un agregado es porque nadie mas vende la carta, y
                        entonces el "desde" tiene que decir de que esta hecho:
                        ese minimo puede ser una japonesa o un reverse suelto.
                      */}
                      {pricing.data.chile.cheapestAggregate && (
                        <span className="muted shop__agg shop__agg--from">
                          {t('aggregateCheapest')}
                        </span>
                      )}
                    </p>
                  )}
                  <ul className="shop">
                    {pricing.data.chile.offers.map((o, i) => {
                      const filas = (
                        <>
                          <span className="shop__title">
                            {/*
                              Con cuatro tiendas mezcladas, un enlace sin
                              nombre no dice a donde va: por eso cada oferta
                              lleva la suya. El respaldo de una sola tienda no
                              la manda por oferta y ahi la fila va pelada.
                            */}
                            {o.shop && <span className="shop__store">{o.shop}</span>}
                            {o.shop && o.variant && <span className="shop__sep">·</span>}
                            {o.variant}
                            {/*
                              La fila de un marketplace no es comparable con
                              "Collector Center · Ingles NM": es el minimo entre
                              vendedores, sin idioma ni condicion. La palabra va
                              EN LA FILA -- pintarla igual que las demas y
                              explicarlo solo al pie seria esconderlo.
                            */}
                            {o.aggregate && <span className="shop__agg">{t('aggregateOffer')}</span>}
                          </span>
                          <span className="shop__price">
                            {clp(o.priceClp)}
                            {!o.available && <em> · {t('outOfStock')}</em>}
                          </span>
                        </>
                      );
                      return (
                        <li key={o.url ?? `${o.variant}-${i}`}>
                          {o.url ? (
                            <a
                              className={`shop__item${o.available ? '' : ' is-out'}`}
                              href={o.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {filas}
                            </a>
                          ) : (
                            <span className={`shop__item shop__item--plano${o.available ? '' : ' is-out'}`}>
                              {filas}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {/*
                    Por que esta lista es mas corta que lo que ensena la
                    tienda: solo sale lo ingles o sin idioma declarado (ver
                    esIngles en chile.js). Sin decirlo, una espanola mas barata
                    vista en la tienda parece un fallo de precios.
                  */}
                  <p className="muted shop__nota">{t('englishOnlyNote')}</p>
                  {/*
                    La pista va AQUI y no en cada fila: dentro del <a> de la
                    oferta, tocarla en el telefono abriria la tienda en vez de
                    explicar nada. Rotulo una sola vez sobre la lista, que es lo
                    que ya se hace con las columnas de probabilidad del mazo.
                  */}
                  {pricing.data.chile.offers.some((o) => o.aggregate) && (
                    <p className="muted shop__nota">
                      {/* Enfocable a proposito: Pista abre al enfocar, y un
                          rotulo que solo se explica con el raton deja fuera al
                          teclado igual que el `title` al telefono. */}
                      <Pista texto={t('aggregateOfferHint')}>
                        <span className="shop__agg" tabIndex={0}>{t('aggregateOffer')}</span>
                      </Pista>{' '}
                      {t('aggregateCheapest')}
                    </p>
                  )}
                </>
              )}

              {historia.length >= 2 && <Tendencia key={idActual} historia={historia} t={t} lang={lang} />}

              {(cm || tpMarket) && (
                <div className="precio__intl">
                  <h3>{t('pricesIntl')}</h3>
                  <div className="precio__intlFilas">
                    {cm && (
                      <p className="price">
                        <span className="price__fuente">Cardmarket</span>
                        <strong>{money(cm.trend ?? cm.avg, cm.unit || 'EUR')}</strong>
                        {pricing.data?.clp?.fromEur != null && (
                          <span className="price__clp">≈ {clp(pricing.data.clp.fromEur)}</span>
                        )}
                      </p>
                    )}
                    {tpMarket && (
                      <p className="price">
                        <span className="price__fuente">TCGplayer</span>
                        <strong>{money(tpMarket.marketPrice, tp.unit || 'USD')}</strong>
                        {pricing.data?.clp?.fromUsd != null && (
                          <span className="price__clp">≈ {clp(pricing.data.clp.fromUsd)}</span>
                        )}
                      </p>
                    )}
                  </div>
                  {pricing.data?.clp?.rate && (
                    <p className="muted price__fx">
                      {t('fxNote')}: USD {pricing.data.clp.rate.usd} · EUR {pricing.data.clp.rate.eur}
                    </p>
                  )}
                </div>
              )}
              </section>

              {card.illustrator && (
                <p className="muted detail__illus">
                  {t('illustrator')}: {card.illustrator}
                </p>
              )}
            </div>

            {/*
              Versiones y linea evolutiva, FUERA de la columna del arte (sep-2026).
              En escritorio siguen debajo de la carta (rejilla con areas); en el
              telefono, apiladas, caian ENTRE la carta y su nombre, y el precio
              quedaba a tres pantallas. Ahora el orden del DOM es el de lectura:
              carta, que es y cuanto vale, y despues sus parientes.
            */}
            {(card.otherPrints?.length > 0 || card.evolution) && !ampliada && (
              <div className="detail__rel">
              {/*
                OTRAS VERSIONES de la misma carta -- full art, shiny, promos --
                como tira de miniaturas bajo el arte, que era la zona vacia.
                Pinchar una cambia la ficha entera a esa impresion (precios
                incluidos: cada version tiene los suyos). Se desplaza en
                horizontal sin barra nativa, como el resto de tiras de la app.
              */}
              {card.otherPrints?.length > 0 && !ampliada && (
                <div className="versiones">
                  <p className="versiones__titulo muted">{t('otherPrints', { n: card.otherPrints.length })}</p>
                  <ul className="versiones__tira" ref={refTira}>
                    {card.otherPrints.map((v2) => (
                      <li key={v2.id}>
                        <button
                          type="button"
                          className="versiones__mini"
                          onClick={() => setVersionId(v2.id)}
                          title={`${v2.setName} · ${v2.localId}${v2.rarity ? ` · ${v2.rarity}` : ''}`}
                        >
                          {cardArt(v2) ? (
                            <img src={cardArt(v2)} alt={`${card.name} — ${v2.setName}`} loading="lazy" />
                          ) : (
                            <span className="versiones__sinArte muted">{v2.localId}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/*
                LINEA EVOLUTIVA: de donde viene y a que evoluciona, con arte y
                clicable. El dato dormia en la API (`evolveFrom`) sin pintarse;
                resolverlo a cartas reales convierte la ficha en un pasillo --
                se recorre la familia entera sin volver a la rejilla. Reusa el
                mecanismo de `versionId`: mismo modal, otra carta dentro.
              */}
              {card.evolution && !ampliada && (
                <div className="evo">
                  <p className="versiones__titulo muted">{t('evolutionLine')}</p>
                  {/* Mismo manejador de rueda que la tira de versiones: la
                      fila ya no envuelve y con raton hay que poder recorrerla. */}
                  <div className="evo__fila" ref={refTira}>
                    {card.evolution.from && (
                      <>
                        <button
                          type="button"
                          className="evo__paso"
                          onClick={() => setVersionId(card.evolution.from.id)}
                          title={card.evolution.from.name}
                        >
                          {cardArt(card.evolution.from) && (
                            <img src={cardArt(card.evolution.from)} alt="" loading="lazy" />
                          )}
                          <span>{card.evolution.from.name}</span>
                        </button>
                        <span className="evo__flecha muted" aria-hidden="true">›</span>
                      </>
                    )}
                    <span className="evo__paso is-actual" aria-current="true">
                      <span>{card.name}</span>
                    </span>
                    {card.evolution.to.length > 0 && (
                      <span className="evo__flecha muted" aria-hidden="true">›</span>
                    )}
                    {card.evolution.to.map((ev) => (
                      <button
                        type="button"
                        key={ev.id}
                        className="evo__paso"
                        onClick={() => setVersionId(ev.id)}
                        title={ev.name}
                      >
                        {cardArt(ev) && <img src={cardArt(ev)} alt="" loading="lazy" />}
                        <span>{ev.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
